import { action, observable, makeObservable } from 'mobx';
import {IServiceFactoryExtended} from '../../../service-factory-extended.interface';
import {CASINOFIED_APP_INIT_TIMEOUT_MS, ICasinofiedApplication, IAppCloseParams} from './casinofied-application.interface';
import {
    AppIdentifiers, ComponentID,
    IDivHostedAppInitParameters, IOpenAppData,
    ITopicSubscription,
    IUserInfoParameters
} from '@sparkware/uc-sdk-core';
import {STANDARD_TOPIC_HEADER} from '../../message-broker/message-broker.consts';
import {ICasinofiedComponent} from '../components/casinofied-component.interface';
import {ICasinofiedComponentFactory} from '../casinofied-app-factory.interfaces';
import {CasinofiedAppSdkLoader} from '../casinofied-app-sdk-loader';
import {
    getCasinofiedCommonAppContextParams, getCasinofiedCommonUserInfo,
    getCasinofiedDeviceInfo,
    getCasinofiedGeoInfoParams
} from '../../casinofied-helper-functions';
import {CasinofiedZoidApplicationAdapter} from "./adapters/casinofied-zoid-application-adapter";
import {ICasinofiedApplicationAdapter} from "./adapters/casinofied-application-adapter.interface";
import {CasinofiedRegularApplicationAdapter} from "./adapters/casinofied-regular-application-adapter";
import {IAppInitResultReportingDetails} from '../../analytics-publisher/casinofied-analytics.service';
import {Check, TimeSpan} from '@sparkware/app-utils';



export abstract class CasinofiedApplicationBase implements ICasinofiedApplication {
    public constructor(openAppData: IOpenAppData,
                       private _componentFactory: ICasinofiedComponentFactory,
                       private readonly appSdkLoader: CasinofiedAppSdkLoader,
                       protected readonly services: IServiceFactoryExtended,
                       protected readonly useZoidIntegration: boolean) {
        makeObservable(this);

        this._openAppData = {
            ...openAppData,
            launchInfo: {
                ...openAppData.launchInfo
            }
        };

        this.appId = this._openAppData.appID;

        //ZOID doesn't like minus sign in the containerID
        this._openAppData.launchInfo.containerID = this.appId + '_' + services.tools.getGuid().replace(/-/g, '_');
    }

    public readonly appId: AppIdentifiers;
    protected readonly _openAppData: IOpenAppData;
    protected _sessionID: string;

    @observable.ref
    isLoading = false;

    @action.bound
    private _setIsLoading(value: boolean): void {
        this.isLoading = value;
    }

    protected _isAnonymousModeAllowed(): boolean {
        return false;
    }


    protected _topicsSubscriptions: ITopicSubscription[] = [];

    protected _getInitAppParams(initParams: IDivHostedAppInitParameters): IDivHostedAppInitParameters {
        return initParams;
    }

    @observable.ref
    protected _appInitFailed = false;

    get shouldPreserveStateUponOAuthStateChange(): boolean {
        return false;
    }

    @action.bound
    protected _setAppInitFailed(): void {
        const rootElement = this.services.dom.getElementById(this._openAppData.launchInfo.containerID)
        if(rootElement) {
            // in case the widget rendered something on the screen but still failed to initialize
            // we want to clean up everything so we can show our own error message
            while(rootElement.firstChild) {
                rootElement.firstChild.remove();
            }
        }
        this._appInitFailed = true;
    }

    get openAppData(): IOpenAppData {
        return this._openAppData;
    }

    private _openPromise = Promise.resolve();
    open(): Promise<void> {
        if(!this._isAnonymousModeAllowed() && !this.services.user.isAuthorized) {
            return Promise.reject(`${this.appId} is not allowed in anonymous mode`);
        }

        this._setIsLoading(true);
        this._openPromise = this.appSdkLoader.loadAppSdk(this.appId).then((widgetUrl) => {
            let appShowStartTime;
            if(this.isDisposed) {
                return Promise.reject(`${this.appId} was disposed`);
            }

            const timeoutValue = this.services.configuration.servicesConfigs.AppLauncher.widgetLoadTimeoutMS || CASINOFIED_APP_INIT_TIMEOUT_MS;

            return new Promise<void>((resolve, reject) => {
                const openTimeout = setTimeout(() => {
                    if(this.isDisposed) {
                        return;
                    }
                    this._setAppInitFailed();
                    this._unsubscribeFromTopics();
                    this.onAppInitTimeout(appInitParams, {errorCode:1, errorDescription: 'Timeout'});
                    reject(`Initializing ${this.appId} application timeout out`);
                }, timeoutValue);


                const {appInitFailed, appInitSucceeded} = this.services.messageBroker.channels.session.topics;
                let appInitParams: IDivHostedAppInitParameters = null;
                const cleanUp = () => {
                    clearTimeout(openTimeout);
                    this._unsubscribeFromTopics();
                }

                this._topicsSubscriptions.push(appInitSucceeded.subscribe((payload) => {
                    if(this._isCurrentApp(payload.appID)) {
                        if(this.isDisposed) {
                            return;
                        }
                        this._sessionID = payload.sessionID;
                        cleanUp();
                        this.onAppInitSuccess(appInitParams, {loadingTime: Date.now() - appShowStartTime});
                        resolve();
                    }
                }));

                this._topicsSubscriptions.push(appInitFailed.subscribe((payload) => {
                    if(this._isCurrentApp(payload.appID)) {
                        if(this.isDisposed) {
                            return;
                        }
                        this._setAppInitFailed();
                        cleanUp();
                        this.onAppInitFailed(appInitParams, {errorCode: Check.isNumber(payload.errorCode) ? payload.errorCode : parseInt(payload.errorCode), errorDescription: payload.errorDescription, loadingTime: (Date.now() - appShowStartTime)});
                        reject(`Failed to open application ${this.appId}; errorCode: ${payload.errorCode}; errorDescription: ${payload.errorDescription}`);
                    }
                }));
                let getAppInitParamsStartTime = Date.now();
                this._getAppInitCommonParams(this._openAppData, widgetUrl).then(commonParams => {
                    this.onGetAppInitParams(commonParams, {loadSubject: "getAppInitParams", duration: Date.now()-getAppInitParamsStartTime, appId: this.appId});
                    if(this._isDisposed) {
                        return Promise.reject(`${this.appId} was disposed`);
                    }
                    appInitParams = commonParams;
                    appShowStartTime = Date.now();
                    return this._showApp(commonParams);
                }).catch(err => {
                    this._setAppInitFailed();
                    cleanUp();
                    this.onAppInitFailed(appInitParams, {errorCode:1, errorDescription: err?.toString()});
                    reject(err);
                });
            });
        }).finally(() => {
            this._openPromise = Promise.resolve();
            this._setIsLoading(false);
        });

        return this._openPromise;

    }

    protected _getAppSdkObjectName(): string {
        const sdkObjectName = this.services.configuration.servicesConfigs.AppLauncher.apps[this.appId].sdk;
        if(sdkObjectName) {
            return sdkObjectName;
        }
        return this.appId;
    }

    waitForAppToOpen() {
        return this._openPromise;
    }

    private _waitForContainerIdToBeInTheDOM(): Promise<void> {
        return new Promise((resolve, reject) => {
            const startTime = Date.now();
            const interval =  setInterval(() => {
                if(this.services.dom.getElementById(this._openAppData.launchInfo.containerID)) {
                    clearInterval(interval);
                    resolve();
                } else if(Date.now() - startTime > TimeSpan.fromSeconds(3).totalMilliseconds) {
                    clearInterval(interval);
                    reject(`containerID creation for widget ${this.appId} timeout`);
                }
            }, 10);
        })
    }

    protected _applicationAdapter: ICasinofiedApplicationAdapter;
    protected async _showApp(initParams: IDivHostedAppInitParameters): Promise<void> {

        const appObject = this.services.window.getGlobalProperty(this._getAppSdkObjectName());
        if(!appObject) {
            throw new Error(`Couldn't find an object for ${this.appId} application`);
        }

        if(!appObject.init) {
            throw new Error(`${this.appId} application doesn't have an init function`);
        }

        this._applicationAdapter = this._createApplicationAdapter(appObject);

        this.onAppInitRequest(initParams);

        await this._waitForContainerIdToBeInTheDOM();
        this._applicationAdapter.init(this._getInitAppParams(initParams));
    }

    protected _createApplicationAdapter(appObject: any): ICasinofiedApplicationAdapter {
        if(this.useZoidIntegration) {
            return new CasinofiedZoidApplicationAdapter(this.appId, appObject, this.services);
        } else {
            return new CasinofiedRegularApplicationAdapter(appObject, this.services);
        }

    }

    private _getUserInfo(): Promise<IUserInfoParameters> {
        let userInfo: IUserInfoParameters = {
            ...getCasinofiedCommonUserInfo(this.services)
        };

        if(!this.services.user.isAuthorized) {
            return Promise.resolve(userInfo);
        }

        return this.services.user.getPlatformToken().then(({token}) => {
            return {...userInfo, token: token, userMode: 'authenticated'};
        });
    }

    private async _getAppInitCommonParams(openAppData: IOpenAppData, widgetUrl: string): Promise<IDivHostedAppInitParameters> {
        let correlationId = openAppData.correlationID || openAppData.launchInfo?.businessCorrelationID || this.services.tools.getGuid()
        const userInfo = await this._getUserInfo();
        const geoInfo = await getCasinofiedGeoInfoParams(this.services);

        const result: IDivHostedAppInitParameters = {
            correlationID: correlationId,
            applicationContext: {
                appID: this.appId,
                launchInfo: {
                    ...openAppData.launchInfo
                },
                productPackageToID: this.services.configuration.servicesConfigs.AppLauncher.apps[this.appId].productPackageID,
                widgetUrl: widgetUrl,
                ...getCasinofiedCommonAppContextParams(this.services)
            },
            messageBrokerChannels: this.services.messageBroker.channels,
            deviceInfo: getCasinofiedDeviceInfo(this.services),
            geoInfo: geoInfo,
            userInfo: userInfo,

        };

        return result;
    }

    private _unsubscribeFromTopics() {
        this._topicsSubscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });

        this._topicsSubscriptions = [];
    }

    private _isDisposed = false;
    public get isDisposed(): boolean {
        return this._isDisposed;
    }

    dispose() {
        this._isDisposed = true;
        this._unsubscribeFromTopics();
        this._miniatureComponent?.remove();
        this._applicationAdapter?.dispose();
    }

    protected _isCurrentApp(appId: string) {
        return this.appId === appId;
    }

    @observable.ref
    private _isMinimized: boolean = false;

    get isMinimized() {
        return this._isMinimized;
    }

    @action.bound
    protected _setIsMinimized(value: boolean): void {
        this._isMinimized = value;
    }

    protected _getMiniatureComponentID(): ComponentID {
        return null;
    }

    private _miniatureComponent: ICasinofiedComponent = null;
    minimize(componentData: any): void {
        if (this._miniatureComponent?.isLoading) {
            return;
        }
        const componentID = this._getMiniatureComponentID();
        if (!componentID) {
            throw new Error(`Application ${this.appId} doesn't have configured the minimization support`);
        }
        this._setIsMinimized(true);

        this.waitForAppToOpen().then(() => {
            this._miniatureComponent = this._componentFactory.createComponent(componentID);
            this._miniatureComponent.show(componentData)
                .catch((err) => {
                    this._miniatureComponent = null;
                    this.services.logger.error(`Failed to show miniature component ${componentID}`, err);
                }).finally(() => {
                if (this.isDisposed) { // if the application was closed meanwhile then we need to remove the bubble.
                    this._miniatureComponent?.remove();
                }
            });
        });
    }

    maximize(): void {
        this._setIsMinimized(false);
        if(this._miniatureComponent) {
            this._miniatureComponent.remove();
            this._miniatureComponent = null;
        }
    }

    protected _publishMinimizeApp() {
        this.services.messageBroker.channels.session.topics.minimizeApp.publish(STANDARD_TOPIC_HEADER, {
            appID: this.appId
        });
    }

    public close(appCloseParams: IAppCloseParams) {
        this.services.messageBroker.channels.session.topics.appClosed.publish(STANDARD_TOPIC_HEADER, {
            appID: this.appId,
            closeReason: appCloseParams.reason,
            launchInfo: {
                ...this._openAppData.launchInfo,
                channel: appCloseParams.channel
            }
        });
    }

    isOpeningInNewWindow(): boolean {
        return false;
    }

    protected onAppInitTimeout(appInitParams: IDivHostedAppInitParameters, appInitResultReportingDetails?: IAppInitResultReportingDetails) {
        this.services.casinofiedAnalytics.appInitResult(appInitParams, appInitResultReportingDetails);
    }
    protected onAppInitSuccess(appInitParams: IDivHostedAppInitParameters, appInitResultReportingDetails?: IAppInitResultReportingDetails) {
        this.services.casinofiedAnalytics.appInitResult(appInitParams, appInitResultReportingDetails);
    }
    protected onAppInitFailed(appInitParams: IDivHostedAppInitParameters, appInitResultReportingDetails?: IAppInitResultReportingDetails) {
        this.services.casinofiedAnalytics.appInitResult(appInitParams, appInitResultReportingDetails);
    }
    protected onGetAppInitParams(commonParams: IDivHostedAppInitParameters, reportData: {loadSubject: string; duration: number; appId: string;}) {
        this.services.casinofiedAnalytics.reportDuration(reportData.loadSubject, reportData.duration, reportData.appId);
    }
    protected onAppInitRequest(appInitParams: IDivHostedAppInitParameters) {
        this.services.casinofiedAnalytics.appInitRequest(appInitParams);
    }
}
